import { Flex, Spinner, Box } from '@chakra-ui/react';
import {
  forwardRef,
  useImperativeHandle,
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { KText } from '@/ui-library/components';
import { useOfferStore } from '../../store';
import { Formik } from 'formik';
import { KAutoComplete } from '@/ui-library/components/organisms/KFields/KAutoComplete';
import { CustomValueContainer } from '@/components/ats/EmailTemplates/EmailTemplates';
import { fetchSelectOptions } from '@/components/formBuilder/helpers/formHelpers';
import { useGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import { getTemplatesCategoryConfig } from '@/constants/templates-category';
import { useKFlags } from '@/hooks/use-k-flags';
import { OfferEmailForm, type OfferEmailFormRef } from './OfferEmailForm';
import '../offer.scss';
import { EsignEmailForm, type EsignEmailFormRef } from './EsignEmailForm';
import { offerDetailsAPI } from '../../api';

export interface OfferEmailRef {
  getModel: () => Promise<{
    hasValidData: boolean;
    payload: any;
  }>;
}

export interface OfferEmailProps {
  type?: 'create' | 'edit';
  selectSignatureType?: 'kula' | 'docusign' | 'pandadoc' | 'adobe';
}

const CATEGORY = ['candidate_offer_notice'];
const CHANNEL = 'email';

export const OfferEmail = forwardRef((props: any, comRef: any) => {
  const { selectSignatureType = 'kula' } = props;

  const offerId = useOfferStore((state) => state.offerId);
  const applicationData = useOfferStore((state) => state.applicationData);
  const currentOffer = useOfferStore((state) => state.currentOffer);
  const formRef = useRef<OfferEmailFormRef>(null);
  const esignFormRef = useRef<EsignEmailFormRef>(null);

  const [templates, setTemplates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<any>({});

  const { feSurvey, feCandidateAvailability } = useKFlags();

  const templatesCategoryConfig = getTemplatesCategoryConfig({
    feSurvey,
    feCandidateAvailability,
  });
  const view = [...templatesCategoryConfig.candidate_adhoc.view, 'offer'];

  const { data: { variables = [] } = {} } = useGetTemplatesVariablesQuery({
    params: { view },
  });

  const contactId = applicationData?.contact?.id;

  const attachments = useMemo(() => {
    // offer PDF
    const offerPDF = currentOffer?.offer_document_pdf?.url
      ? [{ ...currentOffer?.offer_document_pdf, isNonRemovable: true }]
      : [];
    return [...offerPDF, ...(currentOffer?.attachments || [])];
  }, [currentOffer]);

  const buildEndPoint = useMemo(() => {
    const categories = CATEGORY.map(
      (cat) => `&filter_by[category][]=${cat}`
    ).join('');
    return `/ats_comm_templates?filter_by[channel]=${CHANNEL}${categories}&items=200&filter_by[name]=`;
  }, []);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setIsLoading(true);
        const apiConfig = {
          endPoint: buildEndPoint,
        };
        const list = await fetchSelectOptions(apiConfig);
        setTemplates(list || []);
        if (list && list.length > 0) {
          setSelectedTemplate(list[0]);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
        setTemplates([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplates();
  }, [buildEndPoint]);

  useImperativeHandle(comRef, () => ({
    getModel: async () => {
      const element = document.querySelector('#invalid-variables-alert');
      element?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      if (element !== null) {
        return {
          hasValidData: false,
          payload: {},
        };
      }
      if (selectSignatureType === 'kula') {
        if (!formRef.current) {
          return {
            hasValidData: false,
            payload: {},
          };
        }
        return formRef.current.getModel();
      } else {
        if (!esignFormRef.current) {
          return {
            hasValidData: false,
            payload: {},
          };
        }
        return esignFormRef.current.getModel();
      }
    },
  }));

  const selectOnChange = useCallback((value: any) => {
    setSelectedTemplate(value);
  }, []);

  const handleAttachmentRemove = async (file: any) => {
    await offerDetailsAPI.deleteAttachment({
      offerId: currentOffer?.id,
      attachmentId: file?.id,
    });
  };

  if (!offerId || !contactId) {
    return (
      <Flex h="100%" w="100%" justifyContent="center" alignItems="center">
        <Spinner color="primary-600" />
      </Flex>
    );
  }

  const getProviderLabel = () => {
    switch (selectSignatureType) {
      case 'docusign':
        return 'DocuSign';
      case 'pandadoc':
        return 'PandaDoc';
      case 'adobe':
        return 'Adobe';
      default:
        return 'Kula';
    }
  };

  if (selectSignatureType === 'kula') {
    return (
      <Flex
        gap="12px"
        flexDir="column"
        w="100%"
        className="offer-email-container"
        data-test-id="offer-email-container"
      >
        <KText size="lg-semi-bold" color="secondary-900">
          Send offer with Kula
        </KText>
        <KText size="sm-medium" color="secondary-700" mb="8px">
          Compose your email to send the offer letter to the candidate
        </KText>

        {isLoading ? (
          <Flex h="200px" w="100%" justifyContent="center" alignItems="center">
            <Spinner color="primary-600" />
          </Flex>
        ) : (
          <Flex flexDir="column" gap="16px" w="100%">
            <Box>
              <Formik initialValues={{}} onSubmit={() => {}}>
                <KAutoComplete
                  item={
                    {
                      name: 'emailTemplates',
                      type: 'autoComplete',
                      useInput: true,
                      isRequired: true,
                      isMulti: false,
                      placeholder: `Search templates`,
                      labelKey: 'name',
                      valueKey: 'id',
                      customOnChange: selectOnChange,
                      options: templates,
                      updateEndpointOnChange: false,
                      loadDefaultOptions: true,
                      fetchConfig: {
                        method: 'get',
                        endPoint: buildEndPoint,
                        processResponse: (res: any) => {
                          const options = res?.data?.map((item: any) => ({
                            label: item?.name,
                            value: item?.id,
                            ...item,
                          }));
                          return options;
                        },
                      },
                      components: {
                        ValueContainer: CustomValueContainer,
                      },
                    } as any
                  }
                />
              </Formik>
            </Box>

            {/* Email Form */}
            <OfferEmailForm
              ref={formRef}
              selectedTemplate={selectedTemplate}
              attachments={attachments}
              variables={variables}
              onAttachmentRemove={handleAttachmentRemove}
            />
          </Flex>
        )}
      </Flex>
    );
  }

  const integrationTypeMap = {
    docusign: 'docusign',
    pandadoc: 'pandadocs',
    adobe: 'adobe',
  };

  return (
    <Flex
      gap="12px"
      flexDir="column"
      w="100%"
      className="offer-email-container"
      data-test-id="offer-email-container"
    >
      <KText size="lg-semi-bold" color="secondary-900">
        Send offer with {getProviderLabel()}
      </KText>
      <KText size="sm-medium" color="secondary-700" mb="8px">
        Now you can send the offer letter to the candidate using{' '}
        {getProviderLabel()}
      </KText>

      <EsignEmailForm
        ref={esignFormRef}
        integrationType={integrationTypeMap[selectSignatureType] as any}
        isAdmin={false}
      />
    </Flex>
  );
});

OfferEmail.displayName = 'OfferEmail';
